<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="isVisible" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="$emit('close')"></div>

        <!-- Modal -->
        <div class="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 animate-scale-in">
          <!-- Close -->
          <button @click="$emit('close')" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <!-- Icon -->
          <div class="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
            <svg class="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>

          <h3 class="text-lg font-bold text-gray-900 mb-1">Recibe alertas de alojamiento</h3>
          <p class="text-sm text-gray-500 mb-5">
            Te avisamos cuando haya nuevos alojamientos que encajen con tu búsqueda{{ cityLabel ? ` en ${cityLabel}` : '' }}.
          </p>

          <!-- Search summary -->
          <div v-if="searchSummary" class="bg-gray-50 rounded-lg px-4 py-3 mb-5 text-xs text-gray-600">
            <span class="font-medium text-gray-900">Tu búsqueda:</span> {{ searchSummary }}
          </div>

          <!-- Form -->
          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <input
                v-model="email"
                type="email"
                required
                placeholder="tu@email.com"
                class="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                :disabled="isSubmitting"
              />
            </div>

            <label class="flex items-start gap-2 cursor-pointer">
              <input v-model="wantsAlerts" type="checkbox" class="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              <span class="text-xs text-gray-500">Avisarme cuando aparezcan nuevos alojamientos similares</span>
            </label>

            <button
              type="submit"
              :disabled="isSubmitting || !email"
              class="w-full py-3 bg-indigo-600 text-white font-semibold rounded-xl text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <span v-if="isSubmitting">Guardando...</span>
              <span v-else>Guardar búsqueda</span>
            </button>
          </form>

          <p class="text-[10px] text-gray-400 mt-3 text-center">
            Solo usaremos tu email para alertas de alojamiento. Puedes darte de baja en cualquier momento.
          </p>

          <!-- Success state -->
          <div v-if="isSuccess" class="absolute inset-0 bg-white rounded-2xl flex flex-col items-center justify-center p-8">
            <div class="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg class="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-gray-900 mb-1">¡Listo!</h3>
            <p class="text-sm text-gray-500 text-center">Te avisaremos en {{ email }} cuando aparezcan nuevos alojamientos.</p>
            <button @click="$emit('close')" class="mt-5 text-sm font-medium text-indigo-600 hover:text-indigo-700">Cerrar</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref } from 'vue';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const props = defineProps({
  isVisible: { type: Boolean, default: false },
  cityLabel: { type: String, default: '' },
  searchFilters: { type: Object, default: () => ({}) },
  searchSummary: { type: String, default: '' },
  totalResults: { type: Number, default: 0 },
});

const emit = defineEmits(['close', 'saved']);

const email = ref('');
const wantsAlerts = ref(true);
const isSubmitting = ref(false);
const isSuccess = ref(false);

const handleSubmit = async () => {
  if (!email.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await addDoc(collection(db, 'savedSearches'), {
      email: email.value.trim().toLowerCase(),
      filters: props.searchFilters,
      city: props.cityLabel || null,
      totalResultsAtSave: props.totalResults,
      wantsAlerts: wantsAlerts.value,
      createdAt: serverTimestamp(),
      source: 'search_modal',
      notificationsSent: 0,
    });
    isSuccess.value = true;
    emit('saved', { email: email.value });
  } catch (error) {
    console.error('Error saving search:', error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.animate-scale-in {
  animation: scale-in 0.2s ease-out;
}
@keyframes scale-in {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
</style>
